import React, { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";
import { getApiEndpoint } from "../../../../utils/apiConfig";
import axiosWithAuth from "../../../../utils/axiosWithAuth";

const StoreManager = () => {
  const [store, setStore] = useState({
    name: { az: "", en: "", ru: "" },
    phoneNumber: "",
    image: {
      id: null,
      status: true,
    },
    status: true,
  });
  const [stores, setStores] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = () => {
    axiosWithAuth()
      .get("stores/admin")
      .then((response) => {
        // Only show stores with status=true
        const activeStores = response.data.filter(store => store.status === true);
        setStores(activeStores);
      })
      .catch((error) => console.error("Failed to fetch stores:", error));
  };

  const handleInput = (event) => {
    const { name, value, dataset } = event.target;
    const field = dataset.field;

    if (["az", "en", "ru"].includes(name)) {
      setStore((prevType) => ({
        ...prevType,
        [field]: { ...prevType[field], [name]: value },
      }));
    } else {
      setStore((prevType) => ({ ...prevType, [name]: value }));
    }
  };

  const uploadImage = (file) => {
    const formData = new FormData();
    formData.append("file", file);

    return axiosWithAuth().post(
      "mono-files/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  };

  const handleImageUpload = (event) => {
    const fileInput = event.target;
    if (fileInput && fileInput.files && fileInput.files[0]) {
      const file = fileInput.files[0];
      uploadImage(file)
        .then((response) => {
          console.log("Image upload successful:", response.data);
          // Save complete image object including all necessary fields
          setStore((prevStore) => ({
            ...prevStore,
            image: {
              id: response.data.id,
              name: response.data.fileName,
              type: response.data.mimeType,
              path: response.data.url,
              size: response.data.size,
              hashBase64: null, // Will be populated by backend
              status: true
            },
          }));
        })
        .catch((error) => {
          console.error("Failed to upload image:", error);
          swal({
            title: "Error!",
            text: "Image could not be uploaded. Please try again.",
            icon: "error",
          });
        });
    } else {
      console.log("No file selected");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const apiUrl = isEditing
      ? `stores/${editId}`
      : "stores";

    const requestMethod = isEditing ? 'put' : 'post';
    
    axiosWithAuth()
      [requestMethod](apiUrl, store)
      .then(() => {
        setIsEditing(false);
        setEditId(null);
        setStore({
          name: { az: "", en: "", ru: "" },
          phoneNumber: "",
          image: { id: null },
          status: true,
        });
        fetchStores();
        swal({
          title: "",
          text: "Store has been added",
          icon: "success",
          timer: 1500,
        });
      })
      .catch((error) => {
        console.error("Failed to submit store:", error);
        swal({
          title: "",
          text: "Store has not been added",
          icon: "error",
          timer: 1500,
        });
      });
  };

  const handleEdit = (store) => {
    setIsEditing(true);
    setEditId(store.id);
    setStore(store);
  };

  const handleDelete = (storeId) => {
    swal({
      title: "Are you sure?",
      text: "Are you sure you want to delete this store?",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    })
    .then((willDelete) => {
      if (willDelete) {
        axiosWithAuth()
          .delete(`stores/${storeId}`)
          .then((response) => {
            swal({
              title: "Success!",
              text: "Store has been deleted successfully",
              icon: "success",
              timer: 1500,
            });
            fetchStores();
          })
          .catch((error) => {
            console.error("Failed to delete store:", error);
            swal({
              title: "Error!",
              text: "Store could not be deleted",
              icon: "error",
            });
          });
      }
    });
  };

  return (
    <div className="store-manager">
      <h3 className="text-center">Store Manager</h3>
      <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
        <div className="store-name">
          <h5 className="text-center mb-4">Store Information</h5>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label" htmlFor="az">
                Azerbaijani
              </label>
              <input
                className="form-control"
                type="text"
                value={store.name.az}
                onChange={handleInput}
                name="az"
                data-field="name"
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label" htmlFor="en">
                English
              </label>
              <input
                className="form-control"
                type="text"
                value={store.name.en}
                onChange={handleInput}
                name="en"
                data-field="name"
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label" htmlFor="ru">
                Russian
              </label>
              <input
                className="form-control"
                type="text"
                value={store.name.ru}
                onChange={handleInput}
                name="ru"
                data-field="name"
                required
              />
            </div>
            <div className="col-12">
              <label className="form-label" htmlFor="phoneNumber">
                Phone Number
              </label>
              <input
                className="form-control"
                type="text"
                name="phoneNumber"
                value={store.phoneNumber}
                onChange={handleInput}
                required
              />
            </div>
            <div className="col-12">
              <label className="form-label">Store Image</label>
              <input
                className="form-control"
                type="file"
                onChange={handleImageUpload}
                required={!isEditing}
              />
            </div>
          </div>
        </div>

        <button className="btn btn-primary align-self-center px-5">
          {isEditing ? "Save Changes" : "Add Store"}
        </button>
      </form>

      <h5 className="text-center mb-4 mt-4">Stores List</h5>
      <div className="table-responsive">
        <table className="table table-hover m-0">
          <thead>
            <tr>
              <th scope="col" className="text-center" style={{ width: "60px" }}>
                №
              </th>
              <th scope="col">Name (az)</th>
              <th scope="col">Name (en)</th>
              <th scope="col">Name (ru)</th>
              <th scope="col">Phone Number</th>
              <th scope="col">Image</th>
              <th
                scope="col"
                className="text-center"
                style={{ width: "100px" }}
              >
                Edit
              </th>
              <th
                scope="col"
                className="text-center"
                style={{ width: "100px" }}
              >
                Delete
              </th>
            </tr>
          </thead>
          <tbody>
            {stores.map((type, index) => {
              // Determine image display source based on available data
              let imageSource = null;
              if (type.image) {
                if (type.image.hashBase64) {
                  // If we have base64 data, use it directly
                  imageSource = `data:${type.image.type || 'image/png'};base64,${type.image.hashBase64}`;
                } else if (type.image.path) {
                  // If we have a path/url, use it
                  imageSource = type.image.path;
                } else if (type.image.id) {
                  // If we have just an ID, construct download URL
                  imageSource = `${getApiEndpoint()}/mono-files/download/${type.image.id}`;
                }
              }
              
              return (
                <tr key={type.id}>
                  <th scope="row" className="text-center">
                    {index + 1}
                  </th>
                  <td>{type.name.az}</td>
                  <td>{type.name.en}</td>
                  <td>{type.name.ru}</td>
                  <td>{type.phoneNumber}</td>
                  <td>
                    {imageSource ? (
                      <img
                        src={imageSource}
                        width={100}
                        alt={type.name.en}
                        className="img-thumbnail"
                        onError={(e) => {
                          console.error("Image failed to load:", e);
                          e.target.src = `${getApiEndpoint()}/mono-files/download/default.png`;
                          e.target.onerror = null; // Prevent infinite loop
                        }}
                      />
                    ) : (
                      <span className="text-muted">No image</span>
                    )}
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => handleEdit(type)}
                    >
                      <i className="bi bi-pencil-square"></i>
                      Edit
                    </button>
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDelete(type.id)}
                    >
                      <i className="bi bi-trash3"></i>
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StoreManager;
